import React, { useState, useEffect } from "react";
import { useBusiness, api } from "@/App";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, BarChart, Bar } from "recharts";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// Generate placeholder data keys dynamically based on current year
const generatePlaceholderKeys = () => {
  const currentYear = new Date().getFullYear();
  return {
    [`${currentYear}-01`]: { actual_revenue: 12500, actual_expenses: 8200 },
    [`${currentYear}-02`]: { actual_revenue: 14200, actual_expenses: 9100 },
    [`${currentYear}-03`]: { actual_revenue: 11800, actual_expenses: 7800 },
    [`${currentYear}-04`]: { actual_revenue: 15600, actual_expenses: 10200 },
    [`${currentYear}-05`]: { actual_revenue: 13400, actual_expenses: 8900 },
    [`${currentYear}-06`]: { actual_revenue: 16200, actual_expenses: 11500 },
  };
};

// Placeholder bank data - will be replaced with Plaid integration
const PLACEHOLDER_BANK_DATA = generatePlaceholderKeys();

const CashFlowProjection = () => {
  const { selectedBusiness } = useBusiness();
  const [projections, setProjections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [bankConnected, setBankConnected] = useState(false);
  const [showPlaceholder, setShowPlaceholder] = useState(true);
  const [formData, setFormData] = useState(
    MONTHS.map((month, index) => ({
      month: `${new Date().getFullYear()}-${String(index + 1).padStart(2, "0")}`,
      label: month,
      revenue: "",
      expenses: "",
    }))
  );

  useEffect(() => {
    if (selectedBusiness) {
      fetchProjections();
      checkBankConnection();
    }
  }, [selectedBusiness]);

  const checkBankConnection = async () => {
    try {
      const response = await api.get("/plaid/accounts");
      setBankConnected(response.data.length > 0);
    } catch (error) {
      setBankConnected(false);
    }
  };

  const fetchProjections = async () => {
    try {
      const response = await api.get(`/cashflow/${selectedBusiness.id}`);
      if (response.data.length > 0) {
        const existingData = response.data.reduce((acc, p) => {
          acc[p.month] = p;
          return acc;
        }, {});
        
        setFormData(prev => prev.map(item => {
          const existing = existingData[item.month];
          return existing ? {
            ...item,
            revenue: existing.projected_revenue.toString(),
            expenses: existing.projected_expenses.toString(),
          } : item;
        }));
      }
      setProjections(response.data);
    } catch (error) {
      console.error("Failed to fetch projections:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (index, field, value) => {
    setFormData(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const saveProjection = async (index) => {
    const item = formData[index];
    if (!item.revenue && !item.expenses) {
      toast.error("Please enter revenue or expenses");
      return;
    }

    setSaving(true);
    try {
      await api.post("/cashflow", {
        business_id: selectedBusiness.id,
        month: item.month,
        projected_revenue: parseFloat(item.revenue) || 0,
        projected_expenses: parseFloat(item.expenses) || 0,
      });
      toast.success(`${item.label} projection saved!`);
      fetchProjections();
    } catch (error) {
      toast.error("Failed to save projection");
    } finally {
      setSaving(false);
    }
  };

  const saveAllProjections = async () => {
    setSaving(true);
    try {
      for (const item of formData) {
        if (item.revenue || item.expenses) {
          await api.post("/cashflow", {
            business_id: selectedBusiness.id,
            month: item.month,
            projected_revenue: parseFloat(item.revenue) || 0,
            projected_expenses: parseFloat(item.expenses) || 0,
          });
        }
      }
      toast.success("All projections saved!");
      fetchProjections();
    } catch (error) {
      toast.error("Failed to save projections");
    } finally {
      setSaving(false);
    }
  };

  if (!selectedBusiness) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Please select or create a business first</p>
      </div>
    );
  }

  // Get actual bank data (placeholder for now)
  const getActualData = (month) => {
    if (showPlaceholder && PLACEHOLDER_BANK_DATA[month]) {
      return PLACEHOLDER_BANK_DATA[month];
    }
    return { actual_revenue: null, actual_expenses: null };
  };

  const chartData = formData.map((item) => {
    const existing = projections.find(p => p.month === item.month);
    const actuals = getActualData(item.month);
    return {
      name: item.label,
      projected_revenue: existing?.projected_revenue || parseFloat(item.revenue) || 0,
      projected_expenses: existing?.projected_expenses || parseFloat(item.expenses) || 0,
      actual_revenue: actuals.actual_revenue,
      actual_expenses: actuals.actual_expenses,
      projected_profit: (existing?.projected_revenue || parseFloat(item.revenue) || 0) - (existing?.projected_expenses || parseFloat(item.expenses) || 0),
      actual_profit: actuals.actual_revenue && actuals.actual_expenses ? actuals.actual_revenue - actuals.actual_expenses : null,
    };
  });

  const projectedTotals = chartData.reduce(
    (acc, item) => ({
      revenue: acc.revenue + item.projected_revenue,
      expenses: acc.expenses + item.projected_expenses,
      profit: acc.profit + item.projected_profit,
    }),
    { revenue: 0, expenses: 0, profit: 0 }
  );

  const actualTotals = chartData.reduce(
    (acc, item) => ({
      revenue: acc.revenue + (item.actual_revenue || 0),
      expenses: acc.expenses + (item.actual_expenses || 0),
      profit: acc.profit + (item.actual_profit || 0),
    }),
    { revenue: 0, expenses: 0, profit: 0 }
  );

  const variance = {
    revenue: actualTotals.revenue - projectedTotals.revenue,
    expenses: actualTotals.expenses - projectedTotals.expenses,
    profit: actualTotals.profit - projectedTotals.profit,
  };

  return (
    <div className="space-y-8" data-testid="cashflow-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-stone-900">12-Month Cash Flow</h1>
          <p className="text-stone-600">Compare projections vs actuals from bank data</p>
        </div>
        <div className="flex items-center gap-3">
          {!bankConnected && (
            <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
              <span className="mr-1">⚠️</span> Bank not connected
            </Badge>
          )}
          <Button
            variant="outline"
            onClick={() => setShowPlaceholder(!showPlaceholder)}
            className="text-sm"
          >
            {showPlaceholder ? "Hide" : "Show"} Sample Data
          </Button>
        </div>
      </div>

      {/* Bank Connection Banner */}
      {!bankConnected && (
        <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
          <CardContent className="pt-6">
            <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
              <div className="p-3 rounded-full bg-blue-100">
                <svg className="w-6 h-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
                  <path d="M3 21h18M3 10h18M5 6l7-3 7 3M4 10v11M20 10v11M8 14v3M12 14v3M16 14v3" />
                </svg>
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-blue-900">Connect Your Bank for Real Data</h3>
                <p className="text-sm text-blue-700">
                  Link your bank accounts to automatically populate actual revenue and expenses from your transactions.
                  {showPlaceholder && " Currently showing placeholder data for demonstration."}
                </p>
              </div>
              <Button 
                onClick={() => window.location.href = '/bank-accounts'}
                className="bg-blue-600 hover:bg-blue-700"
              >
                Connect Bank
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Summary Cards - Now with Projected vs Actual */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="card-hover">
          <CardContent className="pt-6">
            <p className="text-sm text-muted-foreground mb-2">Revenue</p>
            <div className="space-y-1">
              <div className="flex justify-between">
                <span className="text-xs text-stone-500">Projected:</span>
                <span className="font-semibold text-stone-900">${projectedTotals.revenue.toLocaleString()}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-xs text-stone-500">Actual:</span>
                <span className="font-semibold text-[#1e3a5f]">
                  {showPlaceholder ? `$${actualTotals.revenue.toLocaleString()}` : "—"}
                </span>
              </div>
              {showPlaceholder && (
                <div className={`text-xs text-right ${variance.revenue >= 0 ? 'text-[#1e3a5f]' : 'text-red-600'}`}>
                  {variance.revenue >= 0 ? '↑' : '↓'} ${Math.abs(variance.revenue).toLocaleString()}
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        <Card className="card-hover">
          <CardContent className="pt-6">
            <p className="text-sm text-muted-foreground mb-2">Expenses</p>
            <div className="space-y-1">
              <div className="flex justify-between">
                <span className="text-xs text-stone-500">Projected:</span>
                <span className="font-semibold text-stone-900">${projectedTotals.expenses.toLocaleString()}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-xs text-stone-500">Actual:</span>
                <span className="font-semibold text-amber-600">
                  {showPlaceholder ? `$${actualTotals.expenses.toLocaleString()}` : "—"}
                </span>
              </div>
              {showPlaceholder && (
                <div className={`text-xs text-right ${variance.expenses <= 0 ? 'text-[#1e3a5f]' : 'text-red-600'}`}>
                  {variance.expenses <= 0 ? '↓' : '↑'} ${Math.abs(variance.expenses).toLocaleString()}
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        <Card className="card-hover">
          <CardContent className="pt-6">
            <p className="text-sm text-muted-foreground mb-2">Profit</p>
            <div className="space-y-1">
              <div className="flex justify-between">
                <span className="text-xs text-stone-500">Projected:</span>
                <span className={`font-semibold ${projectedTotals.profit >= 0 ? 'text-[#1e3a5f]' : 'text-red-600'}`}>
                  ${projectedTotals.profit.toLocaleString()}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-xs text-stone-500">Actual:</span>
                <span className={`font-semibold ${actualTotals.profit >= 0 ? 'text-[#1e3a5f]' : 'text-red-600'}`}>
                  {showPlaceholder ? `$${actualTotals.profit.toLocaleString()}` : "—"}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className={`card-hover ${showPlaceholder ? (variance.profit >= 0 ? 'bg-[#1e3a5f]/5 border-[#1e3a5f]/20' : 'bg-red-50 border-red-200') : ''}`}>
          <CardContent className="pt-6">
            <p className="text-sm text-muted-foreground mb-2">Variance (YTD)</p>
            <div className="text-center">
              {showPlaceholder ? (
                <>
                  <p className={`text-2xl font-bold ${variance.profit >= 0 ? 'text-[#1e3a5f]' : 'text-red-600'}`}>
                    {variance.profit >= 0 ? '+' : '-'}${Math.abs(variance.profit).toLocaleString()}
                  </p>
                  <p className="text-xs text-stone-500 mt-1">
                    {variance.profit >= 0 ? 'Above projection' : 'Below projection'}
                  </p>
                </>
              ) : (
                <p className="text-sm text-muted-foreground">Connect bank to see</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Chart - Projected vs Actual */}
      <Card>
        <CardHeader>
          <CardTitle>Projected vs Actual Cash Flow</CardTitle>
          <CardDescription>Compare your projections against actual bank transactions</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e7e5e4" />
                <XAxis dataKey="name" fontSize={12} />
                <YAxis fontSize={12} tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`} />
                <Tooltip formatter={(value) => value ? `$${value.toLocaleString()}` : 'N/A'} />
                <Legend />
                <Bar dataKey="projected_revenue" name="Projected Revenue" fill="#d1d5db" radius={[4, 4, 0, 0]} />
                <Bar dataKey="actual_revenue" name="Actual Revenue" fill="#059669" radius={[4, 4, 0, 0]} />
                <Bar dataKey="projected_expenses" name="Projected Expenses" fill="#fcd34d" radius={[4, 4, 0, 0]} />
                <Bar dataKey="actual_expenses" name="Actual Expenses" fill="#f59e0b" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Data Entry Table with Actuals */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Monthly Cash Flow Details</CardTitle>
              <CardDescription>Enter projections and compare with bank actuals</CardDescription>
            </div>
            <Button
              onClick={saveAllProjections}
              disabled={saving}
              className="bg-[#1e3a5f] hover:bg-[#2d5a8e]"
              data-testid="save-all-projections-btn"
            >
              {saving ? "Saving..." : "Save All"}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="rounded-lg border overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-stone-50">
                  <TableHead className="w-20">Month</TableHead>
                  <TableHead className="text-center" colSpan={2}>
                    <div className="text-[#1e3a5f]">Revenue</div>
                    <div className="flex text-xs font-normal">
                      <span className="flex-1">Projected</span>
                      <span className="flex-1">Actual</span>
                    </div>
                  </TableHead>
                  <TableHead className="text-center" colSpan={2}>
                    <div className="text-amber-600">Expenses</div>
                    <div className="flex text-xs font-normal">
                      <span className="flex-1">Projected</span>
                      <span className="flex-1">Actual</span>
                    </div>
                  </TableHead>
                  <TableHead className="text-center" colSpan={2}>
                    <div>Profit</div>
                    <div className="flex text-xs font-normal">
                      <span className="flex-1">Projected</span>
                      <span className="flex-1">Actual</span>
                    </div>
                  </TableHead>
                  <TableHead className="w-16"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {formData.map((item, index) => {
                  const projectedProfit = (parseFloat(item.revenue) || 0) - (parseFloat(item.expenses) || 0);
                  const actuals = getActualData(item.month);
                  const actualProfit = actuals.actual_revenue && actuals.actual_expenses 
                    ? actuals.actual_revenue - actuals.actual_expenses 
                    : null;
                  
                  return (
                    <TableRow key={item.month}>
                      <TableCell className="font-medium">{item.label}</TableCell>
                      {/* Revenue */}
                      <TableCell>
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          value={item.revenue}
                          onChange={(e) => handleInputChange(index, "revenue", e.target.value)}
                          className="w-24 text-sm"
                          placeholder="0"
                          data-testid={`revenue-${item.label}`}
                        />
                      </TableCell>
                      <TableCell className="text-[#1e3a5f] font-mono text-sm">
                        {actuals.actual_revenue ? `$${actuals.actual_revenue.toLocaleString()}` : '—'}
                      </TableCell>
                      {/* Expenses */}
                      <TableCell>
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          value={item.expenses}
                          onChange={(e) => handleInputChange(index, "expenses", e.target.value)}
                          className="w-24 text-sm"
                          placeholder="0"
                          data-testid={`expenses-${item.label}`}
                        />
                      </TableCell>
                      <TableCell className="text-amber-600 font-mono text-sm">
                        {actuals.actual_expenses ? `$${actuals.actual_expenses.toLocaleString()}` : '—'}
                      </TableCell>
                      {/* Profit */}
                      <TableCell className={`font-mono text-sm ${projectedProfit >= 0 ? 'text-[#1e3a5f]' : 'text-red-600'}`}>
                        ${projectedProfit.toLocaleString()}
                      </TableCell>
                      <TableCell className={`font-mono text-sm ${actualProfit !== null ? (actualProfit >= 0 ? 'text-[#1e3a5f]' : 'text-red-600') : 'text-stone-400'}`}>
                        {actualProfit !== null ? `$${actualProfit.toLocaleString()}` : '—'}
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => saveProjection(index)}
                          disabled={saving}
                          className="text-[#1e3a5f] hover:text-[#1e3a5f] hover:bg-[#1e3a5f]/5 text-xs"
                        >
                          Save
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
                <TableRow className="bg-stone-50 font-medium">
                  <TableCell>Total</TableCell>
                  <TableCell className="font-mono">${projectedTotals.revenue.toLocaleString()}</TableCell>
                  <TableCell className="font-mono text-[#1e3a5f]">
                    {showPlaceholder ? `$${actualTotals.revenue.toLocaleString()}` : '—'}
                  </TableCell>
                  <TableCell className="font-mono">${projectedTotals.expenses.toLocaleString()}</TableCell>
                  <TableCell className="font-mono text-amber-600">
                    {showPlaceholder ? `$${actualTotals.expenses.toLocaleString()}` : '—'}
                  </TableCell>
                  <TableCell className={`font-mono ${projectedTotals.profit >= 0 ? 'text-[#1e3a5f]' : 'text-red-600'}`}>
                    ${projectedTotals.profit.toLocaleString()}
                  </TableCell>
                  <TableCell className={`font-mono ${actualTotals.profit >= 0 ? 'text-[#1e3a5f]' : 'text-red-600'}`}>
                    {showPlaceholder ? `$${actualTotals.profit.toLocaleString()}` : '—'}
                  </TableCell>
                  <TableCell></TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </div>
          {showPlaceholder && !bankConnected && (
            <p className="text-xs text-amber-600 mt-3 flex items-center gap-1">
              <span>⚠️</span> Showing placeholder data. Connect your bank to see real transactions.
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default CashFlowProjection;
